import { useEffect, useMemo, useState } from "react";
import { createPromoCode } from "../../api/mintSpace";
import { useTranslation } from "../../context/TranslationContext";
import { promoErrorText } from "../../utils/promoCodes";
import Button from "../ds/Button";
import { FieldLabel } from "../ds/Field";
import InlineMessage from "../ds/InlineMessage";
import Modal from "../ds/Modal";
import PopUpButton from "../ds/PopUpButton";
import SearchField from "../ds/SearchField";
import Switch from "../ds/Switch";
import TextField from "../ds/TextField";

/**
 * The form for a new promo code, sliding in from the side. Which products it works on
 * is chosen next, on its own page — `onCreated` hands the new code over for that.
 *
 * There's no "discount duration": a discount applies to every payment, because Keepz
 * bills a subscription at one fixed amount and can't lower only the first few.
 */

const CODE_CHARS = /[^A-Z0-9_-]/g;

/** Figma E18: from 600px the popup runs down the window's right edge. */
const DRAWER =
  "min-[600px]:fixed min-[600px]:inset-y-0 min-[600px]:right-0 min-[600px]:max-h-none min-[600px]:rounded-r-none min-[600px]:border-y-0 min-[600px]:border-r-0";

/** A datetime-local value an hour from now, as the earliest sensible expiry. */
const soon = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const EMPTY = {
  code: "",
  discountValue: "",
  discountType: "percentage",
  eligibility: "everyone",
  affiliateUserId: "",
  withExpiry: false,
  expiresAt: "",
  withMax: false,
  maxRedemptions: "",
  oncePerUser: true,
};

export default function CreatePromoCodePanel({ open, isDark, mintSpaceId, affiliates = [], onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // A fresh form each time it opens.
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY);
    setSearch("");
    setError("");
  }, [open]);


  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const shownAffiliates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return affiliates;
    return affiliates.filter((a) =>
      [a.username, a.businessName].some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [affiliates, search]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await createPromoCode(mintSpaceId, {
        code: form.code,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        eligibility: form.eligibility,
        affiliateUserId: form.affiliateUserId || null,
        expiresAt: form.withExpiry && form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        maxRedemptions: form.withMax ? Number(form.maxRedemptions) : 0,
        oncePerUser: form.oncePerUser,
      });
      onCreated?.(res.data);
    } catch (err) {
      setError(promoErrorText(t, "form", err?.code, err?.message || t("promoCodes.form.failed")));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const canSubmit =
    form.code.length >= 3 &&
    Number(form.discountValue) > 0 &&
    (!form.withExpiry || form.expiresAt) &&
    (!form.withMax || Number(form.maxRedemptions) >= 1);

  const switchRow = (label, on, onClick) => (
    <div className="flex items-center justify-between gap-space-8 py-space-12">
      <span className="text-body text-label-primary">{label}</span>
      <Switch on={on} label={label} onClick={onClick} />
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t("promoCodes.form.title")}
      closeLabel={t("common.close")}
      isDark={isDark}
      panelClass={DRAWER}
    >
      <form onSubmit={submit} className="flex flex-1 flex-col gap-space-20">
        <div className="flex flex-1 flex-col gap-space-16">
          <TextField
            autoFocus
            label={t("promoCodes.form.code")}
            hint={t("promoCodes.form.codeHint")}
            value={form.code}
            onChange={(e) =>
              set({ code: e.target.value.toUpperCase().replace(/\s/g, "_").replace(CODE_CHARS, "").slice(0, 32) })
            }
            placeholder="SUMMER_SALE"
          />

          <div className="flex flex-col gap-space-8">
            <FieldLabel htmlFor="promo-discount">{t("promoCodes.form.discount")}</FieldLabel>
            <div className="flex items-center gap-space-8">
              <TextField
                id="promo-discount"
                type="number"
                inputMode="decimal"
                min="0"
                max={form.discountType === "percentage" ? 100 : undefined}
                step="0.01"
                value={form.discountValue}
                onChange={(e) => set({ discountValue: e.target.value })}
                placeholder={form.discountType === "percentage" ? "10 %" : "10 ₾"}
                className="min-w-0 flex-1"
              />
              <PopUpButton
                label={t("promoCodes.form.discountType")}
                value={form.discountType}
                onChange={(discountType) => set({ discountType })}
                options={[
                  { value: "percentage", label: t("promoCodes.form.percentage") },
                  { value: "fixed", label: t("promoCodes.form.fixed") },
                ]}
                className="shrink-0"
              />
            </div>
            <p className="text-footnote text-label-tertiary">{t("promoCodes.form.appliesEvery")}</p>
          </div>

          <div className="flex flex-col gap-space-8">
            <p className="text-footnote-semibold text-label-secondary">{t("promoCodes.form.eligibility")}</p>
            <PopUpButton
              label={t("promoCodes.form.eligibility")}
              value={form.eligibility}
              onChange={(eligibility) => set({ eligibility })}
              options={[
                { value: "everyone", label: t("promoCodes.form.everyone") },
                { value: "new", label: t("promoCodes.form.newUsers") },
                { value: "churned", label: t("promoCodes.form.churnedUsers") },
              ]}
              className="w-full"
            />
            {form.eligibility !== "everyone" && (
              <p className="text-footnote text-label-tertiary">
                {t(form.eligibility === "new" ? "promoCodes.form.newHint" : "promoCodes.form.churnedHint")}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-space-8">
            <p className="text-footnote-semibold text-label-secondary">{t("promoCodes.form.affiliate")}</p>
            <p className="text-footnote text-label-tertiary">{t("promoCodes.form.affiliateHint")}</p>
            {affiliates.length > 0 ? (
              <>
                {affiliates.length > 5 && (
                  <SearchField
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("promoCodes.form.searchAffiliates")}
                  />
                )}
                <PopUpButton
                  label={t("promoCodes.form.affiliate")}
                  value={form.affiliateUserId}
                  onChange={(affiliateUserId) => set({ affiliateUserId })}
                  placeholder={t("promoCodes.form.noAffiliate")}
                  // The chosen one stays listed even when the search hides it.
                  options={affiliates
                    .filter((a) => a.id === form.affiliateUserId || shownAffiliates.includes(a))
                    .map((a) => ({
                      value: a.id,
                      label: a.businessName ? `${a.businessName} (@${a.username})` : `@${a.username}`,
                    }))}
                  className="w-full"
                />
              </>
            ) : (
              <p className="rounded-radius-md bg-fill-quaternary p-space-12 text-subheadline text-label-secondary">
                {t("promoCodes.form.noAffiliates")}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            {switchRow(t("promoCodes.form.setExpiry"), form.withExpiry, () =>
              set({ withExpiry: !form.withExpiry, expiresAt: form.withExpiry ? "" : form.expiresAt || soon() })
            )}
            {form.withExpiry && (
              <TextField
                type="datetime-local"
                min={soon().slice(0, 10) + "T00:00"}
                value={form.expiresAt}
                onChange={(e) => set({ expiresAt: e.target.value })}
                style={{ colorScheme: isDark ? "dark" : "light" }}
              />
            )}
            {switchRow(t("promoCodes.form.setMax"), form.withMax, () => set({ withMax: !form.withMax }))}
            {form.withMax && (
              <TextField
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={form.maxRedemptions}
                onChange={(e) => set({ maxRedemptions: e.target.value.replace(/[^0-9]/g, "") })}
                placeholder="100"
              />
            )}
            {switchRow(t("promoCodes.form.oncePerUser"), form.oncePerUser, () => set({ oncePerUser: !form.oncePerUser }))}
          </div>

          {error && <InlineMessage tone="danger">{error}</InlineMessage>}
        </div>

        <div className="flex justify-end gap-space-12">
          <Button variant="gray" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={busy} disabled={!canSubmit}>
            {t("promoCodes.form.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
